/**
 * This is a custom button component extended from ShadedUI that supports a tooltip when the button is disabled.
 *
 * @see https://ui.shadcn.com/docs/components/button
 */
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"
import { Tooltip, TooltipTrigger, TooltipContent } from "./tooltip"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "text-white primary-gradient shadow-lg flex items-center rounded-md relative overflow-hidden group transition-all ease-out duration-300 disabled:opacity-50 disabled:pointer-events-none",
        primary: "text-white bg-primary flex items-center rounded-md relative overflow-hidden group transition-all ease-out duration-300 disabled:opacity-50 disabled:pointer-events-none",
        outlinePrimary: "text-primary bg-white shadow-sm border border-orange-200 hover:shadow-lg hover:border-orange-400 cursor-pointer flex items-center rounded-md relative overflow-hidden group transition-all ease-out duration-300 disabled:opacity-50 disabled:pointer-events-none",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground text-custom-text-primary",
        red:
          "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-red-200 bg-red-100 text-red-500 hover:text-red-600 hover:bg-red-200 transition-colors h-9 rounded-md px-3 gap-2 ml-4",
        white:
          "border border-input bg-white hover:bg-accent hover:text-gray-600 text-gray-500",
        green:
          "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-green-200 bg-green-300 text-green-800 hover:text-green-700 hover:bg-green-400 transition-colors h-9 rounded-md px-3 gap-2 ml-4",
        secondary:
          "border border-gray-300 dark:border-gray-700 hover:bg-gray-100 hover:dark:bg-gray-800 text-custom-text-primary",
        // Figma "button / secondary": soft grey fill + hairline border, 8px
        // radius. Used for chip-style choices (e.g. the chat empty-state
        // starter chips). Pairs with `size="xs"`; `sm`/`lg` set their own
        // `rounded-md` and would flatten the corners.
        secondarySoft:
          "rounded-lg border border-border bg-muted text-custom-text-primary hover:bg-accent hover:text-accent-foreground transition-colors",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-custom-text-secondary underline-offset-4 hover:underline",
        black: "bg-foreground text-background hover:bg-foreground/90 transition-colors",
        badge: "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border border-custom-border text-custom-text-secondary hover:bg-gray-50 hover:text-custom-text-primary",
        gradient: "relative text-custom-text-primary rounded-md overflow-hidden [&>*]:relative [&>*]:z-10 before:absolute before:inset-0 before:bg-gradient-action before:opacity-10 hover:before:opacity-20 before:transition-opacity border-[0.5px] border-custom-border",
        "ai-primary": "bg-ai-element-button text-white hover:bg-ai-element-button-hover transition-colors",
        "ai-secondary": "border-half border-custom-border bg-white text-ai-element-button hover:bg-gray-50 transition-colors",
        cardAction: "bg-white border border-border/50 text-foreground hover:bg-muted/50",
        cardLink: "text-muted-foreground hover:text-foreground text-left w-fit",
        overlay: "bg-white/60 border border-black/10 shadow-sm hover:bg-white/80",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-7 px-2 py-1 text-xs",
        // Compact solid-action size (h-8 / 13px text / 14px icon). Shared by the
        // SplitButton's `xs` segments and standalone "pill" actions (e.g. the
        // Knowledge Hub "Edit details" CTA) so they read identically.
        compact: "h-8 gap-1.5 px-2.5 text-[13px] [&_svg]:size-3.5",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        iconSm: "h-6 w-6",
      },
      disabledWithTooltip: {
        true: "opacity-100 cursor-not-allowed relative",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean,
  tooltipText?: string,
  icon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, disabledWithTooltip = false, tooltipText, icon, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const buttonElement = (
      <Comp
        className={cn(buttonVariants({ variant, size, disabledWithTooltip, className }))}
        ref={ref}
        {...props}
      >
        {props.children}
      </Comp>
    )

    if (disabledWithTooltip) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {buttonElement}
          </TooltipTrigger>
          <TooltipContent>
            <div className="flex items-center">
              {tooltipText}
              {icon && <span className="ml-2">{icon}</span>}
            </div>
          </TooltipContent>
        </Tooltip>
      )
    }

    return buttonElement
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
